/* eslint-disable react-refresh/only-export-components, max-lines */
import { DimeSeparator, DimeToggleGroup, DimeToggleGroupItem } from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/toggle-group')({
  component: ToggleGroupDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({
  children,
  className,
}: Readonly<{ children: React.ReactNode; className?: string }>) {
  return (
    <div
      className={`flex flex-wrap items-center gap-4 rounded-lg border border-border bg-background p-6 ${className ?? ''}`}
    >
      {children}
    </div>
  );
}

function SingleExample() {
  return (
    <DimeToggleGroup
      type="single"
      defaultValue="center"
      variant="outline"
      aria-label="Text alignment"
    >
      <DimeToggleGroupItem value="left">Left</DimeToggleGroupItem>
      <DimeToggleGroupItem value="center">Center</DimeToggleGroupItem>
      <DimeToggleGroupItem value="right">Right</DimeToggleGroupItem>
    </DimeToggleGroup>
  );
}

function MultipleExample() {
  return (
    <DimeToggleGroup type="multiple" variant="outline" aria-label="Text formatting">
      <DimeToggleGroupItem value="bold">Bold</DimeToggleGroupItem>
      <DimeToggleGroupItem value="italic">Italic</DimeToggleGroupItem>
      <DimeToggleGroupItem value="underline">Underline</DimeToggleGroupItem>
    </DimeToggleGroup>
  );
}

function OutlineExample() {
  return (
    <DimeToggleGroup type="single" variant="outline" aria-label="Text alignment outline">
      <DimeToggleGroupItem value="left">Left</DimeToggleGroupItem>
      <DimeToggleGroupItem value="center">Center</DimeToggleGroupItem>
      <DimeToggleGroupItem value="right">Right</DimeToggleGroupItem>
    </DimeToggleGroup>
  );
}

function SpacingExample() {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-2 text-xs text-muted-foreground">spacing=0 (connected)</p>
        <DimeToggleGroup type="single" spacing={0} variant="outline" aria-label="Spacing 0">
          <DimeToggleGroupItem value="left">Left</DimeToggleGroupItem>
          <DimeToggleGroupItem value="center">Center</DimeToggleGroupItem>
          <DimeToggleGroupItem value="right">Right</DimeToggleGroupItem>
        </DimeToggleGroup>
      </div>
      <div>
        <p className="mb-2 text-xs text-muted-foreground">spacing=2 (default)</p>
        <DimeToggleGroup type="single" spacing={2} variant="outline" aria-label="Spacing 2">
          <DimeToggleGroupItem value="left">Left</DimeToggleGroupItem>
          <DimeToggleGroupItem value="center">Center</DimeToggleGroupItem>
          <DimeToggleGroupItem value="right">Right</DimeToggleGroupItem>
        </DimeToggleGroup>
      </div>
    </div>
  );
}

function VerticalExample() {
  return (
    <DimeToggleGroup type="single" orientation="vertical" variant="outline" aria-label="View mode">
      <DimeToggleGroupItem value="list">List</DimeToggleGroupItem>
      <DimeToggleGroupItem value="grid">Grid</DimeToggleGroupItem>
      <DimeToggleGroupItem value="table">Table</DimeToggleGroupItem>
    </DimeToggleGroup>
  );
}

function FontWeightSelector() {
  const [value, setValue] = React.useState('400');
  const weights = [
    { value: '100', label: 'Thin' },
    { value: '200', label: 'Extra Light' },
    { value: '300', label: 'Light' },
    { value: '400', label: 'Regular' },
    { value: '500', label: 'Medium' },
    { value: '600', label: 'Semi Bold' },
    { value: '700', label: 'Bold' },
    { value: '800', label: 'Extra Bold' },
    { value: '900', label: 'Black' },
  ];
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted-foreground">
        Selected weight: <span style={{ fontWeight: +value }}>{value}</span>
      </p>
      <DimeToggleGroup
        type="single"
        variant="outline"
        value={value}
        onValueChange={(val) => {
          if (val) setValue(val);
        }}
        aria-label="Font weight"
        className="flex-wrap"
      >
        {weights.map((weight) => (
          <DimeToggleGroupItem
            key={weight.value}
            value={weight.value}
            style={{ fontWeight: +weight.value }}
          >
            {weight.label}
          </DimeToggleGroupItem>
        ))}
      </DimeToggleGroup>
    </div>
  );
}

function ToggleGroupDocs() {
  return (
    <div className="max-w-3xl">
      <h1 className="mb-1 text-3xl font-bold">Toggle Group</h1>
      <p className="mb-8 text-muted-foreground">
        A set of two-state buttons that can be toggled on or off. Use{' '}
        <code className="font-mono text-sm">type="single"</code> for exclusive selection or{' '}
        <code className="font-mono text-sm">type="multiple"</code> for multi-select. Compose{' '}
        <code className="font-mono text-sm">DimeToggleGroupItem</code> inside{' '}
        <code className="font-mono text-sm">DimeToggleGroup</code>.
      </p>

      <DocsSection title="Single">
        <Preview>
          <SingleExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          With <code className="font-mono">type="single"</code>, only one item can be active at a
          time. Use <code className="font-mono">value</code> /{' '}
          <code className="font-mono">defaultValue</code> (a string) to control selection.
        </p>
      </DocsSection>

      <DocsSection title="Multiple">
        <Preview>
          <MultipleExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          With <code className="font-mono">type="multiple"</code>, any number of items can be
          active. Use <code className="font-mono">value</code> /{' '}
          <code className="font-mono">defaultValue</code> (a string array) to control selection.
        </p>
      </DocsSection>

      <DocsSection title="Outline">
        <Preview>
          <OutlineExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">variant="outline"</code> to the group to apply bordered
          styling to all items. Other examples on this page use outline by default.
        </p>
      </DocsSection>

      <DocsSection title="Spacing">
        <Preview>
          <SpacingExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">spacing</code> to control the gap between items.{' '}
          <code className="font-mono">spacing=0</code> produces connected (pill-style) items;{' '}
          default is <code className="font-mono">2</code>.
        </p>
      </DocsSection>

      <DocsSection title="Sizes">
        <Preview>
          {(['sm', 'default', 'lg'] as const).map((size) => (
            <DimeToggleGroup
              key={size}
              type="single"
              variant="outline"
              size={size}
              aria-label={`Size ${size}`}
            >
              <DimeToggleGroupItem value="a">A</DimeToggleGroupItem>
              <DimeToggleGroupItem value="b">B</DimeToggleGroupItem>
            </DimeToggleGroup>
          ))}
        </Preview>
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Size</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              { size: 'sm', desc: 'Small — h-7.' },
              { size: 'default', desc: 'Default — h-8.' },
              { size: 'lg', desc: 'Large — h-9.' },
            ].map((row) => (
              <tr key={row.size} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{row.size}</td>
                <td className="py-2 text-muted-foreground">{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>

      <DocsSection title="Vertical">
        <Preview>
          <VerticalExample />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">orientation="vertical"</code> to stack items vertically.
        </p>
      </DocsSection>

      <DocsSection title="Disabled">
        <Preview>
          <DimeToggleGroup type="single" variant="outline" disabled aria-label="Disabled group">
            <DimeToggleGroupItem value="a">A</DimeToggleGroupItem>
            <DimeToggleGroupItem value="b">B</DimeToggleGroupItem>
          </DimeToggleGroup>
          <DimeToggleGroup type="single" variant="outline" aria-label="Partially disabled group">
            <DimeToggleGroupItem value="a">Enabled</DimeToggleGroupItem>
            <DimeToggleGroupItem value="b" disabled>
              Disabled
            </DimeToggleGroupItem>
          </DimeToggleGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">disabled</code> to the group to disable all items, or to
          individual <code className="font-mono">DimeToggleGroupItem</code> to disable selectively.
        </p>
      </DocsSection>

      <DocsSection title="Custom — Font Weight Selector">
        <Preview>
          <FontWeightSelector />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Controlled example showing a font weight picker. Pass{' '}
          <code className="font-mono">value</code> and{' '}
          <code className="font-mono">onValueChange</code> for full control.
        </p>
      </DocsSection>

      <DocsSection title="Props">
        <p className="mb-3 text-sm font-medium">DimeToggleGroup</p>
        <table className="mb-6 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Prop</th>
              <th className="py-2 pr-4 font-medium">Type</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              { prop: 'type', type: '"single" | "multiple"', desc: 'Selection mode. Required.' },
              { prop: 'value', type: 'string | string[]', desc: 'Controlled selected value(s).' },
              {
                prop: 'defaultValue',
                type: 'string | string[]',
                desc: 'Initial value (uncontrolled).',
              },
              {
                prop: 'onValueChange',
                type: '(value) => void',
                desc: 'Fires on selection change.',
              },
              { prop: 'variant', type: '"default" | "outline"', desc: 'Passed to all items.' },
              { prop: 'size', type: '"default" | "sm" | "lg"', desc: 'Passed to all items.' },
              {
                prop: 'spacing',
                type: 'number',
                desc: 'Gap between items (default 2, 0 = connected).',
              },
              { prop: 'disabled', type: 'boolean', desc: 'Disables all items.' },
              { prop: 'orientation', type: '"horizontal" | "vertical"', desc: 'Layout direction.' },
            ].map((row) => (
              <tr key={row.prop} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{row.prop}</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">{row.type}</td>
                <td className="py-2 text-muted-foreground">{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mb-3 text-sm font-medium">DimeToggleGroupItem</p>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Prop</th>
              <th className="py-2 pr-4 font-medium">Type</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              { prop: 'value', type: 'string', desc: 'Unique identifier for this item. Required.' },
              { prop: 'disabled', type: 'boolean', desc: 'Disables this item only.' },
            ].map((row) => (
              <tr key={row.prop} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{row.prop}</td>
                <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">{row.type}</td>
                <td className="py-2 text-muted-foreground">{row.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>
    </div>
  );
}
